import { clsx, type ClassValue } from "clsx"
import { twMerge } from "tailwind-merge"

/** Merge conditional class names, resolving Tailwind conflicts last-wins. */
export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/** Strip a leading/trailing slash so URL segments join predictably. */
export function trimSlashes(value: string) {
  return value.replace(/^\/+|\/+$/g, "")
}

export function absoluteUrl(base: string, path = "") {
  const cleanBase = base.replace(/\/+$/, "")
  const cleanPath = path ? `/${trimSlashes(path)}` : ""
  return `${cleanBase}${cleanPath}`
}

/** Deterministic pick — used to vary decorative accents without Math.random. */
export function pickBySeed<T>(items: readonly T[], seed: string | number): T {
  const key = String(seed)
  let hash = 0
  for (let i = 0; i < key.length; i += 1) {
    hash = (hash * 31 + key.charCodeAt(i)) >>> 0
  }
  return items[hash % items.length]
}

export function unique<T>(items: T[]): T[] {
  return Array.from(new Set(items))
}

export function chunk<T>(items: T[], size: number): T[][] {
  const out: T[][] = []
  for (let i = 0; i < items.length; i += size) out.push(items.slice(i, i + size))
  return out
}

/**
 * Persian-aware slugifier. Keeps Arabic/Persian letters intact (they are valid
 * in modern URLs) and collapses everything else to single hyphens.
 */
export function slugify(input: string) {
  return input
    .trim()
    .toLowerCase()
    .replace(/[‌\s_]+/g, "-")
    .replace(/[^\p{L}\p{N}-]+/gu, "")
    .replace(/-{2,}/g, "-")
    .replace(/^-|-$/g, "")
}

/** Rough Persian reading-time estimate (~200 words/min for Farsi prose). */
export function readingTimeMinutes(text: string) {
  const words = text.trim().split(/\s+/).filter(Boolean).length
  return Math.max(1, Math.round(words / 200))
}

/** Strip markdown/HTML down to plain text for excerpts and meta descriptions. */
export function toPlainText(input: string) {
  return input
    .replace(/<[^>]+>/g, " ")
    .replace(/!\[[^\]]*\]\([^)]*\)/g, " ")
    .replace(/\[([^\]]*)\]\([^)]*\)/g, "$1")
    .replace(/[#*_`>~-]/g, " ")
    .replace(/\s+/g, " ")
    .trim()
}

export function excerpt(input: string, max = 160) {
  const text = toPlainText(input)
  if (text.length <= max) return text
  return `${text.slice(0, max).replace(/\s+\S*$/, "")}…`
}
